
/* ============================== src/hooks/useTaskNotes.js ============================== */
// src/hooks/useTaskNotes.js
/**
 * Личные заметки исполнителя по задаче. Видны только автору
 * (existing.notes[ur.id]) — группировка by user_id в TaskDto
 * (toTaskDto). Сохраняются отдельно от формы задачи через
 * POST/PATCH/DELETE /tasks/:id/notes (React Query), поэтому не входят
 * в initialValues (stripNotes в useTaskFormState).
 *
 * Миграция с DataStore: раньше здесь были store.upsertTaskNote /
 * store.deleteTaskNote, но DataStore удалён, ModalRenderer не передаёт
 * store → store === undefined → TypeError при любой попытке сохранить
 * или удалить заметку. Теперь прямые мутации useCreateTaskNoteMutation /
 * useUpdateTaskNoteMutation / useDeleteTaskNoteMutation; onSuccess в
 * каждой инвалидирует ["tasks"] и ["task", id] — TaskModal держит
 * existing из useTasksQuery, без рефетча existing.notes[ur.id] не
 * обновится.
 *
 * Разделение create/update — по полю id в noteData, которое NoteEditorModal
 * кладёт: { id: note?.id, title, text }. Для новой заметки id === undefined
 * → POST, для существующей → PATCH /tasks/:id/notes/:noteId.
 */
import { useCallback, useState } from "react";
import {
  useCreateTaskNoteMutation,
  useUpdateTaskNoteMutation,
  useDeleteTaskNoteMutation,
} from "../api/queries/tasks";

export function useTaskNotes({ existing, ur, toast }) {
  const rawNotes = existing?.notes?.[ur.id];
  const notesList = Array.isArray(rawNotes) ? rawNotes : [];

  const [editingNote, setEditingNote] = useState(null);

  const createMutation = useCreateTaskNoteMutation();
  const updateMutation = useUpdateTaskNoteMutation();
  const deleteMutation = useDeleteTaskNoteMutation();

  const openNewNote = useCallback(
    () => setEditingNote({ title: "", text: "" }),
    [],
  );
  const openExistingNote = useCallback(
    (note) => setEditingNote({ ...note }),
    [],
  );
  const closeNoteEditor = useCallback(() => setEditingNote(null), []);

  const handleSaveNote = useCallback(
    async (noteData) => {
      if (!existing) return;
      const body = {
        title: (noteData.title || "").trim(),
        text: (noteData.text || "").trim(),
      };
      try {
        if (noteData.id) {
          await updateMutation.mutateAsync({
            id: existing.id,
            noteId: noteData.id,
            body,
          });
        } else {
          await createMutation.mutateAsync({ id: existing.id, body });
        }
        setEditingNote(null);
        toast("Заметка сохранена", "success");
      } catch (err) {
        toast(err.message || "Не удалось сохранить заметку", "error");
      }
    },
    [existing, createMutation, updateMutation, toast],
  );

  const handleDeleteNote = useCallback(
    async (noteId) => {
      if (!existing) return;
      try {
        await deleteMutation.mutateAsync({ id: existing.id, noteId });
        setEditingNote(null);
        toast("Заметка удалена", "info");
      } catch (err) {
        toast(err.message || "Не удалось удалить заметку", "error");
      }
    },
    [existing, deleteMutation, toast],
  );

  return {
    notesList,
    editingNote,
    openNewNote,
    openExistingNote,
    closeNoteEditor,
    handleSaveNote,
    handleDeleteNote,
  };
}
